import { useState } from "react";
import type { Option, Turn } from "../api/types";
import { useI18n } from "../i18n";

type OptionListProps = {
  turn: Turn;
  /** Only the latest turn is interactive. */
  active: boolean;
  busy: boolean;
  onSelect: (message: string, displayText: string) => void;
};

const optionValue = (o: Option): string =>
  o.value !== undefined && o.value !== null ? String(o.value) : o.text;

/**
 * In-feed option buttons (single- or multi-select). Quick-send chips for
 * free-text questions are rendered separately, above the input bar.
 */
export function OptionList({ turn, active, busy, onSelect }: OptionListProps) {
  const { t } = useI18n();
  const [selected, setSelected] = useState<number[]>([]);
  const options = turn.options ?? [];
  if (options.length === 0 || turn.crisis || turn.allow_free_text) return null;

  if (turn.multi_select) {
    // A "None of the above" option is mutually exclusive with the others.
    const noneIdx = options.findIndex((o) => /\bnone\b/i.test(o.text));
    const toggle = (idx: number) =>
      setSelected((cur) => {
        const selecting = !cur.includes(idx);
        if (idx === noneIdx) {
          return selecting ? [idx] : []; // picking "None" clears everything else
        }
        let next = selecting ? [...cur, idx] : cur.filter((i) => i !== idx);
        if (selecting && noneIdx !== -1) next = next.filter((i) => i !== noneIdx); // drop "None"
        return next;
      });
    const submit = () => {
      if (selected.length === 0) return;
      const message = selected.map((i) => optionValue(options[i])).join("|");
      const display = selected.map((i) => options[i].text).join(", ");
      onSelect(message, display);
    };
    return (
      <>
        <div className="options-container" role="group" aria-label="Select all that apply">
          {options.map((o, idx) => (
            <button
              key={o.text}
              type="button"
              className={`selection-option-btn${selected.includes(idx) ? " selected" : ""}`}
              aria-pressed={selected.includes(idx)}
              disabled={!active || busy}
              onClick={() => toggle(idx)}
            >
              {o.text}
            </button>
          ))}
        </div>
        <div className="submit-options-container">
          <button
            type="button"
            className="options-continue-btn"
            disabled={!active || busy || selected.length === 0}
            onClick={submit}
          >
            {t("option.continue")}
          </button>
        </div>
      </>
    );
  }

  // A STATEMENT that waits for a click is the opening acknowledge ("Got it,
  // I'm ready to begin"). It is a call to action, not a choice between
  // options, so it gets the filled brand treatment rather than the quiet
  // outlined chip every other option uses — the whole point is that it should
  // read as the thing to press (Yvett, 17 Aug).
  const isCta = turn.node_type === "STATEMENT" && options.length === 1;

  return (
    <div className="options-container">
      {options.map((o) => (
        <button
          key={o.text}
          type="button"
          className={isCta ? "selection-option-btn cta" : "selection-option-btn"}
          disabled={!active || busy}
          onClick={() => onSelect(optionValue(o), o.text)}
        >
          {o.text}
        </button>
      ))}
    </div>
  );
}
